import { Badge, Group, Stack, Text, ThemeIcon, Timeline, Tooltip } from '@mantine/core';
import {
  IconArrowBackUp,
  IconCalendarEvent,
  IconCheck,
  IconCircleCheck,
  IconFilePlus,
  IconFileUpload,
  IconHistory,
  IconMessage2,
  IconMessageCheck,
  IconMessageQuestion,
  IconPackage,
  IconPalette,
  IconPencil,
  IconSend,
  IconUserPlus,
  IconX,
} from '@tabler/icons-react';
import { WORKFLOW_EVENT_LABELS, type WorkflowEventDto, type WorkflowEventType } from '@mexp/shared';
import { formatDateTime, fromNow } from '@/lib/format';

/** Icon and colour per event type (shared with the request History card). */
export const EVENT_ICONS: Partial<Record<WorkflowEventType, { icon: typeof IconCheck; color: string }>> = {
  request_created: { icon: IconFilePlus, color: 'brand' },
  request_updated: { icon: IconPencil, color: 'gray' },
  request_cancelled: { icon: IconX, color: 'gray' },
  request_completed: { icon: IconCircleCheck, color: 'teal' },
  bd_reviewer_assigned: { icon: IconUserPlus, color: 'violet' },
  item_added: { icon: IconUserPlus, color: 'blue' },
  item_reassigned: { icon: IconUserPlus, color: 'blue' },
  item_updated: { icon: IconPencil, color: 'gray' },
  item_cancelled: { icon: IconX, color: 'gray' },
  target_proposed: { icon: IconCalendarEvent, color: 'yellow' },
  target_accepted: { icon: IconCalendarEvent, color: 'teal' },
  target_rejected: { icon: IconCalendarEvent, color: 'orange' },
  item_submitted: { icon: IconFileUpload, color: 'yellow' },
  item_resubmitted: { icon: IconFileUpload, color: 'yellow' },
  item_verified: { icon: IconCheck, color: 'teal' },
  item_returned: { icon: IconArrowBackUp, color: 'orange' },
  item_reopened: { icon: IconArrowBackUp, color: 'orange' },
  bd_sent: { icon: IconSend, color: 'violet' },
  bd_resent: { icon: IconSend, color: 'violet' },
  bd_approved: { icon: IconCircleCheck, color: 'teal' },
  bd_returned: { icon: IconArrowBackUp, color: 'orange' },
  client_changes_requested: { icon: IconMessage2, color: 'grape' },
  query_compliance_sent: { icon: IconCircleCheck, color: 'teal' },
  clarification_requested: { icon: IconMessageQuestion, color: 'orange' },
  clarification_answered: { icon: IconMessageCheck, color: 'teal' },
  sample_requested: { icon: IconPackage, color: 'brand' },
  sample_updated: { icon: IconPackage, color: 'cyan' },
  artwork_updated: { icon: IconPalette, color: 'violet' },
};

export function EventTimeline({ events }: { events: WorkflowEventDto[] }) {
  if (!events.length) {
    return (
      <Text c="dimmed" fz="sm">
        Nothing has happened yet.
      </Text>
    );
  }
  return (
    <Timeline bulletSize={28} lineWidth={2}>
      {[...events].reverse().map((e) => {
        const meta = EVENT_ICONS[e.type] ?? { icon: IconHistory, color: 'gray' };
        const reason = typeof e.data.reason === 'string' ? e.data.reason : null;
        const note = typeof e.data.note === 'string' ? e.data.note : typeof e.data.notes === 'string' ? e.data.notes : null;
        return (
          <Timeline.Item
            key={e.id}
            bullet={
              <ThemeIcon size={28} radius="xl" color={meta.color} variant="light">
                <meta.icon size={16} />
              </ThemeIcon>
            }
            title={
              <Group gap={6}>
                <Text fz="sm" fw={600}>
                  {WORKFLOW_EVENT_LABELS[e.type]}
                </Text>
                {e.workItemCode && (
                  <Badge size="xs" variant="outline" color="gray" ff="monospace">
                    {e.workItemCode}
                  </Badge>
                )}
              </Group>
            }
          >
            <Stack gap={2}>
              {e.message && (
                <Text fz="sm" c="dimmed">
                  {e.message}
                </Text>
              )}
              {reason && (
                <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  “{reason}”
                </Text>
              )}
              {note && !reason && (
                <Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                  Note: {note}
                </Text>
              )}
              <Tooltip label={formatDateTime(e.createdAt)}>
                <Text fz="xs" c="dimmed" w="fit-content">
                  {e.actor?.name ?? 'System'} · {fromNow(e.createdAt)}
                </Text>
              </Tooltip>
            </Stack>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}
